/*
 * Do not remove or alter the notices in this preamble.
 * This software code regards iO Custom Software.
 * Copyright © 2025 iO The Netherlands B.V. and/or its affiliates.
 * www.iodigital.com All rights reserved. License grant and user rights and obligations
 * according to applicable license agreement. Please contact legal@iodigital.com for
 * questions regarding license and user rights.
 */

import { t } from 'i18next';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';

import { ConfigurationProductDto, ConfigurationVariantDto2 } from '@/api-sdk';
import { iconExclamationTriangle } from '@/components/icons';
import { BREAKPOINT_SMALL_PX } from '@/settings';
import { baseStyles } from '@/styles';
import { LayoutType } from '@/types';
import { ReducedVariants } from './utils';

import '@/components/image/fg-image';

interface ColorVariants {
  [key: string]: {
    code: string;
    color: string;
    image: ConfigurationVariantDto2['image'];
    sizeVariants: ConfigurationVariantDto2[];
    validationMessage: ConfigurationVariantDto2['validationMessage'];
  };
}

@customElement('fg-configuration-variant-quantity-table')
export class FgConfigurationQuantityTable extends LitElement {
  /**
   * Property for product data needed for the product variant code
   *
   * @property {ConfigurationProductDto} product
   */
  @property({ type: Object })
  product?: ConfigurationProductDto;

  /**
   * Property for  configuration variant data which is used in the table
   *
   * @property {ConfigurationVariantDto2} variants
   */
  @property({ type: Array })
  variants: ConfigurationVariantDto2[] = [];

  /**
   * Property for receiving the reduced variants
   *
   * @property {Object} layout
   */
  @property({ type: Object })
  reducedVariants?: ReducedVariants;

  /**
   * Locale string, and is used to fetch the price breakdown data
   *
   * @property {string} locale
   */
  @property({ type: String })
  locale = 'nl_NL';

  /**
   * Layout type {@link LayoutType}, and is used to render the right theme, components and fetch the right price breakdown data
   *
   * @property {LayoutType} layout
   */
  @property({ type: String })
  layout: LayoutType = 'retail';

  renderColorNameAndImage({
    code,
    variantCode,
    color,
    image,
  }: {
    code?: string;
    variantCode?: ConfigurationProductDto['code'];
    color?: string;
    image?: ConfigurationVariantDto2['image'];
  } = {}) {
    if (!color) {
      return null;
    }

    return html`
      <div class="color-image">
        ${image
          ? html`
              <div class="image">
                <fg-image
                  alt=${color}
                  height="64"
                  src=${image}
                  width="64"
                ></fg-image>
              </div>
            `
          : nothing}

        <span>
          ${color}
          <br />
          <span class="variantcode">${variantCode}-${code}</span>
        </span>
      </div>
    `;
  }

  renderTableHeader({
    reducedVariants,
  }: {
    reducedVariants?: ReducedVariants;
  }) {
    if (!reducedVariants) {
      return null;
    }

    const { colors } = reducedVariants;

    return html`
      <tr class="head">
        <th class="color">
          ${t('labels.color')}
          <span>(${colors.length})</span>
        </th>
        <th>
          <span class="visually-hidden">${t('labels.quantity')}</span>
        </th>
        <th class="priceperunit">${t('labels.pricePerUnitInclPrintCost')}</th>
        <th class="total">${t('labels.totalPrice')}</th>
      </tr>
    `;
  }

  render() {
    const colorVariants = this.variants.reduce(
      (colorVariants: ColorVariants, variant) => {
        const { code, image, color, validationMessage } = variant;

        if (!color) {
          return colorVariants;
        }

        colorVariants[color.code] = colorVariants[color.code] || {};

        colorVariants[color.code] = {
          code,
          color: color.name,
          image,
          sizeVariants: [
            ...(colorVariants[color.code].sizeVariants || []),
            variant,
          ],
          validationMessage,
        };
        return colorVariants;
      },
      {},
    );

    return html`
      <table>
        <thead>
          ${this.renderTableHeader({ reducedVariants: this.reducedVariants })}
        </thead>

        <tbody>
          ${Object.values(colorVariants).map((variant, index) => {
            const { code, color, image, sizeVariants, validationMessage } =
              variant;
            const variantCode = this.product?.code;
            const first: boolean = index === 0;

            return html`
              <tr class="color">
                <td colspan="3">
                  ${this.renderColorNameAndImage({
                    code,
                    variantCode,
                    color,
                    image,
                  })}
                </td>
              </tr>
              ${this.renderTableHeader({
                reducedVariants: this.reducedVariants,
              })}

              <tr
                class=${`
                  body
                  ${first ? 'first' : ''}
                `}
              >
                <td class="color">
                  ${this.renderColorNameAndImage({
                    code,
                    variantCode,
                    color,
                    image,
                  })}
                </td>

                <td colspan="3">
                  <table>
                    ${sizeVariants.map(sizeVariant => {
                      const {
                        size,
                        quantity,
                        allInTotalPrice,
                        allInUnitPrice,
                        version,
                      } = sizeVariant;

                      return html`
                        <tr class=${validationMessage ? 'notify' : ''}>
                          <td>${size?.name} ${version?.name}</td>
                          <td class="priceperunit">
                            ${quantity} ${t('labels.quantityPcs')} &times;
                            ${allInUnitPrice.formatted}
                          </td>
                          <td class="total">${allInTotalPrice.formatted}</td>
                        </tr>
                        ${validationMessage
                          ? html`
                              <tr>
                                <td colspan="3">
                                  <span class="notification">
                                    <span>${iconExclamationTriangle}</span>
                                    ${validationMessage}
                                  </span>
                                </td>
                              </tr>
                            `
                          : nothing}
                      `;
                    })}
                  </table>
                </td>
              </tr>
            `;
          })}
        </tbody>
      </table>
    `;
  }

  static styles = [
    baseStyles,
    css`
      .mobile-hidden {
        display: none;
      }
      .mobile-show {
        display: table-cell;
      }

      table {
        margin-top: 15px;
        table-layout: fixed;
        width: 100%;
      }

      thead {
        display: none;
      }

      .head th {
        font-size: 0.75rem;
      }

      th,
      tbody td {
        padding: 9px;
        text-align: right;
      }

      th {
        font-size: 0.875rem;
        font-weight: 400;
      }

      th.color,
      td.color {
        display: none;
      }

      tbody tr.color td {
        border: none;
      }

      th.priceperunit,
      th.total,
      td.total {
        text-align: right;
      }

      tbody td {
        border-bottom: 1px solid
          var(--fg-gray--200, var(--fg-fallback-gray--200));
      }

      tbody tr:last-child td,
      tbody table td {
        border-bottom: none;
      }

      tbody tr.head th {
        padding-top: 5px;
        padding-bottom: 0;
        vertical-align: bottom;
      }

      tbody tr.body > td:last-child {
        padding-right: 0;
        padding-left: 0;
      }

      tbody table {
        margin: 0;
      }

      tbody table td {
        padding-top: 2px;
        padding-bottom: 2px;
      }

      tr.notify {
        color: var(--fg-gray--300, var(--fg-fallback-gray--300));
      }

      .variantcode {
        color: var(--fg-gray--600, var(--fg-fallback-gray--600));
        display: inline-block;
        margin-top: 2px;
      }

      .color-image {
        align-items: start;
        display: flex;
        gap: 8px;
      }

      .color-image .image {
        aspect-ratio: 1 / 1;
        background: var(--fg-gray--100, var(--fg-fallback-gray--100));
        border-radius: 8px;
        display: grid;
        flex-shrink: 0;
        overflow: hidden;
        place-content: center;
        position: relative;
        width: 72px;
      }

      .color-image span {
        padding-top: 2px;
        text-align: left;
      }

      .color-image fg-image::part(image) {
        aspect-ratio: 1 / 1;
        border-radius: 8px;
        height: auto;
        object-fit: contain;
        width: 64px;
      }

      .notification {
        align-items: center;
        color: var(--fg-warning--dark, var(--fg-fallback-warning--dark));
        display: flex;
        gap: 8px;
        font-size: 0.75rem;
        justify-content: flex-end;
        margin-top: 8px;
      }

      @media screen and (min-width: ${BREAKPOINT_SMALL_PX}px) {
        .mobile-hidden {
          display: table-cell;
        }
        .mobile-show {
          display: none;
        }

        thead {
          display: table-header-group;
        }

        .head {
          background: var(
            --fg-tertiary--light,
            var(--fg-fallback-tertiary--light)
          );
        }

        .head th:first-of-type {
          border-top-left-radius: 4px;
          border-bottom-left-radius: 4px;
        }

        .head th:last-of-type {
          border-top-right-radius: 4px;
          border-bottom-right-radius: 4px;
        }

        .head th {
          font-size: 0.875rem;
        }

        th,
        tbody td {
          font-size: 0.875rem;
          padding: 16px;
          vertical-align: top;
        }

        td.total {
          font-size: 1rem;
          font-weight: 500;
        }

        th.color,
        td.color {
          display: table-cell;
          text-align: left;
          width: 28%;
        }
        td.color {
          padding-left: 0;
        }

        tbody {
          font-size: 1rem;
        }

        tbody tr.head,
        tbody tr.color {
          display: none;
        }

        .color-image .image {
          width: 72px;
        }

        .priceperunit {
          width: 180px;
        }

        .mobile-show {
          display: none;
        }
        .mobile-hidden {
          display: table-cell;
        }
      }
    `,
  ];
}
